import React from 'react';
import { checkPermission } from 'HelperFunctions/permissionUtils';
import { paymentsUrl } from 'HelperFunctions/urls';
import {
  Team,
  Company,
  Items,
  Export,
  Revenue,
  PriceTag,
  Percentage,
  Profit,
  Receipt,
  Smiley,
  Document,
  Roles,
  QuickbooksCircle,
  TapwiseIcon,
  Business,
  RentalSettings,
  Payment,
  Contacts,
  Zapier,
  Connect,
  Email,
  LegalDocument,
  TruckDropOff,
  Print,
  TickMark,
} from 'Components/utils/SvgIcons';

export const divideIntoThreeSubarrays = (array) => {
  const remaining = [...array];
  let result = [];
  for (let i = 3; i > 0; i--) {
    result.push(remaining.splice(0, Math.ceil(remaining.length / i)));
  }
  return result;
};

const reportLinks = (
  multilocationReportsEnabled = false,
  generalLedgerEnabled = false,
  robustAndCustomReports = false,
  quickbooksIntegration = false,
  npsCustomerSurveys = false,
  tapwiseEnabled = false
) => {
  return [
    robustAndCustomReports && {
      icon: <Export />,
      to: '/admin/reports/export',
      id: 'custom_export',
      name: 'Custom Export',
      noMobile: true,
    },
    quickbooksIntegration && {
      icon: <QuickbooksCircle />,
      to: '/admin/reports/quickbooks',
      name: 'QuickBooks',
    },
    {
      icon: <Revenue />,
      to: '/admin/reports/revenue',
      name: 'Revenue',
    },
    {
      icon: <PriceTag />,
      to: '/admin/reports/payments',
      name: 'Payments',
    },
    {
      icon: <Percentage />,
      to: '/admin/reports/salesTax/newSalesTaxReport',
      name: 'Sales Tax',
    },
    {
      icon: <Items />,
      to: '/admin/reports/popularItems',
      name: 'Popular Items',
    },
    {
      icon: <Profit />,
      to: '/admin/reports/profit',
      id: 'profitability',
      name: 'Profitability',
    },
    npsCustomerSurveys && {
      icon: <Smiley />,
      to: '/admin/reports/nps',
      id: 'customer_survey',
      name: 'Customer Survey',
    },
    {
      icon: <Document />,
      to: '/admin/reports/documents',
      name: 'Documents',
    },
    {
      icon: <Receipt />,
      to: '/admin/reports/billing',
      id: 'billing_status',
      name: 'Billing Status',
    },
    {
      icon: <Items />,
      to: '/admin/reports/inventory',
      name: 'Inventory',
    },
    {
      icon: <Roles />,
      to: '/admin/reports/staffing',
      name: 'Staffing',
    },
    {
      icon: <Receipt />,
      to: '/admin/reports/total_order_volume',
      name: 'Total Order Volume',
    },
    {
      icon: <Team />,
      to: '/admin/reports/commission',
      name: 'Commission',
    },
    multilocationReportsEnabled && {
      icon: <Company />,
      to: '/admin/reports/multilocation',
      name: 'Multilocation Reports',
    },
    generalLedgerEnabled && {
      icon: <Profit />,
      to: '/admin/reports/generalLedger',
      name: 'General Ledger',
    },
    tapwiseEnabled && checkPermission('view_powerbi_reports') && {
      icon: <TapwiseIcon />,
      to: '/admin/reports/tapwise',
      name: 'TapWise',
    },
  ].filter(Boolean);
};

const settingsLinks = ({
  hasPermission,
  permissions = {},
  paymentProcessor,
}) => {
  // Reports hides while the ledger is mappable. Inverted on purpose: this is not
  // equivalent to `generalLedgerMappable || simplifiedGeneralLedger`.
  const generalLedgerMappable =
    hasPermission('gl_mapping_admin') &&
    hasPermission('access_settings_reports') &&
    permissions.generalLedgerEnabled;

  return [
    hasPermission('access_settings_location') && {
      key: 'location',
      id: 'nav_admin_settings_location',
      to: '/admin/settings/location',
      icon: <Business />,
      name: 'Location',
    },
    hasPermission('access_settings_account') && {
      key: 'account',
      id: 'nav_admin_settings_account',
      to: '/admin/settings/account',
      icon: <Receipt />,
      name: 'Account',
    },
    hasPermission('access_settings_team') && {
      key: 'team',
      id: 'nav_admin_settings_team',
      to: '/admin/settings/team',
      icon: <Team />,
      name: 'Team',
    },
    hasPermission('access_settings_order') && {
      key: 'order',
      id: 'nav_admin_settings_order',
      to: '/admin/settings/order',
      icon: <RentalSettings />,
      name: 'Order',
    },
    hasPermission('access_settings_payments') && {
      key: 'payments',
      id: 'nav_admin_settings_payments',
      to: paymentsUrl(paymentProcessor),
      icon: <Payment />,
      name: 'Payment Processing',
    },
    !generalLedgerMappable &&
      permissions.simplifiedGeneralLedger && {
        key: 'reports',
        id: 'nav_admin_settings_reports',
        to:
          permissions.generalLedgerEnabled || permissions.simplifiedGeneralLedger
            ? '/admin/settings/reports/generalLedger'
            : '/upgrade/generalLedger',
        icon: <Profit />,
        name: 'Reports',
      },
    hasPermission('access_settings_inventory') && {
      key: 'inventory',
      id: 'nav_admin_settings_inventory',
      to: '/admin/settings/inventory',
      icon: <Items />,
      name: 'Inventory',
    },
    hasPermission('access_settings_contacts') && {
      key: 'contacts',
      id: 'nav_admin_settings_contacts',
      to: '/admin/settings/contacts',
      icon: <Contacts />,
      name: 'Contacts',
    },
    hasPermission('access_settings_zapier') && {
      key: 'zapier',
      id: 'nav_admin_settings_zapier',
      to: '/admin/settings/zapier',
      icon: <Zapier />,
      name: 'Zapier',
    },
    permissions.tapgoodsConnect &&
      hasPermission('access_settings_tg_connect') && {
        key: 'tgConnect',
        id: 'nav_admin_settings_tapgoods_connect',
        to: '/admin/settings/connectDefaults',
        icon: <Connect />,
        name: 'TapGoods Connect',
      },
    hasPermission('access_settings_financial') && {
      key: 'financial',
      id: 'nav_admin_settings_financial_settings',
      to: '/admin/settings/financialSettings',
      icon: <RentalSettings />,
      name: 'Financial Settings',
    },
    hasPermission('access_settings_email') && {
      key: 'email',
      id: 'nav_admin_settings_email',
      to: '/admin/settings/email',
      icon: <Email />,
      name: 'Email',
    },
    hasPermission('access_settings_legal') && {
      key: 'legal',
      id: 'nav_admin_settings_legal',
      to: '/admin/settings/legal',
      icon: <LegalDocument />,
      name: 'Legal',
    },
    hasPermission('access_settings_routing') &&
      permissions.routingAndTrackingEnabled && {
        key: 'routing',
        id: 'nav_admin_settings_routing',
        to: '/admin/settings/routing',
        icon: <TruckDropOff />,
        name: 'Routing',
      },
    hasPermission('access_settings_terminology') && {
      key: 'terminology',
      id: 'nav_admin_settings_terminology',
      to: '/admin/settings/terminology',
      icon: <LegalDocument />,
      name: 'Terminology',
    },
    {
      key: 'print',
      to: '/admin/settings/print',
      icon: <Print />,
      name: 'Print',
    },
    hasPermission('access_settings_picklist') && {
      key: 'picklist',
      id: 'nav_admin_settings_picklist',
      to: '/admin/settings/picklist',
      icon: <TickMark />,
      name: 'Picklist',
    },
    permissions.tapwiseEnabled && {
      key: 'tapwise',
      id: 'nav_admin_settings_tapwise',
      to: '/admin/settings/tapwise',
      icon: <TapwiseIcon />,
      name: 'TapWise',
    },
  ].filter(Boolean);
};

export const settingsLinkColumns = (options) =>
  divideIntoThreeSubarrays(settingsLinks(options));

export const reportLinkColumns = (
  multilocationReportsEnabled = false,
  generalLedgerEnabled = false,
  robustAndCustomReports = false,
  quickbooksIntegration = false,
  npsCustomerSurveys = false,
  tapwiseEnabled = false
) => {
  return divideIntoThreeSubarrays(
    reportLinks(
      multilocationReportsEnabled,
      generalLedgerEnabled,
      robustAndCustomReports,
      quickbooksIntegration,
      npsCustomerSurveys,
      tapwiseEnabled
    )
  );
};
